import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  PowerSettingsNew as ToggleIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAPIClients,
  useCreateAPIClient,
  useDeactivateAPIClient,
  useActivateAPIClient,
  useRotateAPIClientKey,
  useDeleteAPIClient,
} from '../../api/admin';
import AdminPageHeader from './AdminPageHeader';

interface APIClient {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  secret_hint: string;
  created_at: string;
  updated_at: string;
}

interface APIClientCreateResponse extends APIClient {
  api_key: string;
}

interface RotateKeyResponse {
  api_key: string;
  secret_hint: string;
}

interface KeyRevealDialogProps {
  open: boolean;
  onClose: () => void;
  apiKey: string;
  title: string;
}

function KeyRevealDialog({ open, onClose, apiKey, title }: KeyRevealDialogProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon color="warning" />
          {title}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Copy this key now — it <strong>will not be shown again</strong>.
          </Alert>

          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
            API KEY
          </Typography>
          <TextField
              fullWidth
              value={apiKey}
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: 13 },
                endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                        <IconButton size="small" onClick={copy}>
                          {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
          />

          <Typography variant="caption" color="text.secondary">
            Pass this key as a Bearer token on each request:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              Authorization: Bearer &lt;your-api-key&gt;
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            I've saved this key
          </Button>
        </DialogActions>
      </Dialog>
  );
}

interface CreateKeyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (client: APIClientCreateResponse) => void;
}

function CreateKeyDialog({ open, onClose, onCreate }: CreateKeyDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const createAPIClient = useCreateAPIClient();

  const handleClose = () => {
    setName('');
    setDescription('');
    setError('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setError('');
    try {
      const data = await createAPIClient.mutateAsync({
        name: name.trim(),
        description: description.trim(),
      });
      onCreate(data);
      handleClose();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Could not create the key.');
    }
  };

  return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create key</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              A new key will be generated. You'll see it once — make sure to copy it.
            </DialogContentText>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
                autoFocus
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Partner Integration"
                required
            />
            <TextField
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional — what this key is used for"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={createAPIClient.isPending}>Cancel</Button>
            <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={createAPIClient.isPending}
                startIcon={createAPIClient.isPending ? <CircularProgress size={16} /> : <AddIcon />}
            >
              Create key
            </Button>
          </DialogActions>
        </form>
      </Dialog>
  );
}

type ConfirmType = 'rotate' | 'toggle' | 'delete';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor?: 'error' | 'warning' | 'success';
  loading: boolean;
}

function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel, confirmColor = 'error', loading }: ConfirmDialogProps) {
  return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
              onClick={onConfirm}
              color={confirmColor}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
  );
}

export default function APIClientsManagement() {
  const queryClient = useQueryClient();
  const { data: clients = [], isLoading } = useAPIClients();
  const deactivateClient = useDeactivateAPIClient();
  const activateClient = useActivateAPIClient();
  const rotateKey = useRotateAPIClientKey();
  const deleteClient = useDeleteAPIClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [keyDialog, setKeyDialog] = useState<{
    open: boolean;
    apiKey: string;
    title: string;
  }>({ open: false, apiKey: '', title: '' });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: ConfirmType | null;
    target: APIClient | null;
  }>({ open: false, type: null, target: null });

  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error' = 'success') =>
      setToast({ open: true, message, severity });

  const handleCreated = (newClient: APIClientCreateResponse) => {
    queryClient.invalidateQueries({ queryKey: ['api-clients'] });
    setKeyDialog({
      open: true,
      apiKey: newClient.api_key,
      title: 'Key created',
    });
  };

  const handleConfirmAction = async () => {
    const { type, target } = confirmDialog;
    if (!target) return;

    try {
      if (type === 'rotate') {
        const data: RotateKeyResponse = await rotateKey.mutateAsync(target.id.toString());
        queryClient.invalidateQueries({ queryKey: ['api-clients'] });
        setConfirmDialog({ open: false, type: null, target: null });
        setKeyDialog({
          open: true,
          apiKey: data.api_key,
          title: 'Key rotated',
        });
      } else if (type === 'toggle') {
        if (target.is_active) {
          await deactivateClient.mutateAsync(target.id.toString());
          showToast('Key disabled. Requests using it are now refused.');
        } else {
          await activateClient.mutateAsync(target.id.toString());
          showToast('Key enabled.');
        }
        queryClient.invalidateQueries({ queryKey: ['api-clients'] });
        setConfirmDialog({ open: false, type: null, target: null });
      } else if (type === 'delete') {
        await deleteClient.mutateAsync(target.id.toString());
        queryClient.invalidateQueries({ queryKey: ['api-clients'] });
        showToast('Key deleted.');
        setConfirmDialog({ open: false, type: null, target: null });
      }
    } catch (err: unknown) {
      showToast((err as { message?: string })?.message || 'Something went wrong.', 'error');
    }
  };

  const isActionLoading =
      deactivateClient.isPending ||
      activateClient.isPending ||
      rotateKey.isPending ||
      deleteClient.isPending;

  const confirmProps: Record<ConfirmType, Omit<ConfirmDialogProps, 'open' | 'onClose' | 'onConfirm' | 'loading'>> = {
    rotate: {
      title: 'Rotate key?',
      message: `"${confirmDialog.target?.name}" gets a new key and the current one stops working immediately. Anything still using the old key will fail until you update it.`,
      confirmLabel: 'Rotate',
      confirmColor: 'warning',
    },
    toggle: {
      title: confirmDialog.target?.is_active ? 'Disable key?' : 'Enable key?',
      message: confirmDialog.target?.is_active
          ? `Requests using "${confirmDialog.target?.name}" are refused from now on. You can enable it again later.`
          : `"${confirmDialog.target?.name}" will accept requests again.`,
      confirmLabel: confirmDialog.target?.is_active ? 'Disable' : 'Enable',
      confirmColor: confirmDialog.target?.is_active ? 'warning' : 'success',
    },
    delete: {
      title: 'Delete key?',
      message: `"${confirmDialog.target?.name}" will be deleted. This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
    },
  };

  if (isLoading) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
    );
  }

  return (
      <Box>
        <AdminPageHeader
            title="API keys"
            description="Keys for scripts and systems that call the Mitigate API. A key is shown once, when you create or rotate it."
            audience="admins"
            action={
              <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
              >
                Create key
              </Button>
            }
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Key ends with</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No keys yet. Create one to let a script or system call the API.
                      </Typography>
                    </TableCell>
                  </TableRow>
              ) : (
                  clients.map((client: APIClient) => (
                      <TableRow key={client.id} sx={{ opacity: client.is_active ? 1 : 0.6 }}>
                        <TableCell>
                          <Typography fontWeight={500}>{client.name}</Typography>
                          {client.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {client.description}
                              </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            ••••••••{client.secret_hint}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                              label={client.is_active ? 'Enabled' : 'Disabled'}
                              color={client.is_active ? 'success' : 'default'}
                              size="small"
                              variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(client.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Rotate">
                            <span>
                              <IconButton
                                  size="small"
                                  disabled={!client.is_active}
                                  onClick={() => setConfirmDialog({ open: true, type: 'rotate', target: client })}
                              >
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={client.is_active ? 'Disable' : 'Enable'}>
                            <IconButton
                                size="small"
                                color={client.is_active ? 'warning' : 'success'}
                                onClick={() => setConfirmDialog({ open: true, type: 'toggle', target: client })}
                            >
                              <ToggleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => setConfirmDialog({ open: true, type: 'delete', target: client })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateKeyDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreate={handleCreated}
        />

        <KeyRevealDialog
            open={keyDialog.open}
            onClose={() => setKeyDialog((s) => ({ ...s, open: false }))}
            apiKey={keyDialog.apiKey}
            title={keyDialog.title}
        />

        {confirmDialog.open && confirmDialog.type && (
            <ConfirmDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, type: null, target: null })}
                onConfirm={handleConfirmAction}
                loading={isActionLoading}
                {...confirmProps[confirmDialog.type]}
            />
        )}

        <Snackbar
            open={toast.open}
            autoHideDuration={4000}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
              severity={toast.severity}
              onClose={() => setToast((t) => ({ ...t, open: false }))}
              variant="filled"
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
  );
}
